import { Injectable } from '@angular/core';

import { getAppVersion, getBuildVersion, getDeviceManufacturer, getDeviceModel, getPlatformName } from 'qp-utility';
import { environment } from '../../../../environments/environment';
import { DataService } from '../../../data.service';
import { LocalStorageKeys } from "../../../shared/LocalStorageKeys";
import { SessionStorageKeys } from "../../../shared/SessionStorageKeys";
import { ErrorEvents } from '../../../shared/models/ErrorEvents';
import { ErrorBase } from '../../../shared/modules/errors/model/ErrorBase';
import { QPError } from '../../../shared/modules/errors/model/QPError';
import { DeviceHandlerService } from '../../../shared/services/device-handler/device-handler.service';
import { getSelectedDisplayLangName, getSelectedWatchLangName } from "../../../shared/utils/LanguageUtils";
import { isLoggedIn } from "../../../shared/utils/LoginUtils";
import { getProfileType } from '../../../shared/utils/profileUtils';
import { UserType } from '../enums/UserType';
import {
  AppInfo,
  DeviceInfo,
  ErrorInfo,
  LanguageInfo,
  TimeInfo,
  UserInfo,
} from '../models/newrelic.model';
import { LocalStorageService } from '../../../shared/services/local-storage/local-storage.service';

declare const window: Window &
  typeof globalThis & {
    startTime: number;
  };
@Injectable({
  providedIn: 'root',
})
export class NewrelicStore {
  constructor(
    private data: DataService,
    private readonly deviceHandler: DeviceHandlerService,
    private readonly localStorage: LocalStorageService
  ) {}

  public getDeviceInfo(): DeviceInfo {
    const newTxt = navigator.userAgent.split('(');
    const userAgentData = [];
    for (let i = 1; i < newTxt.length; i++) {
      userAgentData.push(newTxt[i].split(')')[0]);
    }

    return {
      device: userAgentData[0],
      deviceID: this.deviceHandler.getCurrentDeviceID(),
      qpGlobalID: this.deviceHandler.getCurrentDeviceID(),
      deviceGroup: 'TV',
      deviceManufacturer: getDeviceManufacturer(),
      deviceModel: getDeviceModel(),
      platformName: getPlatformName()
    };
  }

  public getTimeInfo(): TimeInfo {
    let intTimeToLoadStorefront = 0;

    try {
      this.data.storefrontTime = new Date().getTime() - this.data.storefrontTime;
      intTimeToLoadStorefront = sessionStorage.getItem(SessionStorageKeys.ELAPSED_STOREFRONT)
        ? parseInt(sessionStorage.getItem(SessionStorageKeys.ELAPSED_STOREFRONT), 10)
        : 0;
    } catch (e) {
      intTimeToLoadStorefront = 0;
    }
    return {
      timeToAppConfig: this.data.configTime,
      timeToLoadStorefront: intTimeToLoadStorefront,
    };
  }

  public getTimeSinceLoadInfo(): { timeSinceLoad: number} {
    const timeNow = new Date().getTime();
    return {
      timeSinceLoad: window.startTime ? timeNow - window.startTime : 0 ,
    }
  }

  public getApplicationInfo(): AppInfo {
    return {
      appVersion: getAppVersion(),
      appBuild: getBuildVersion(),
      asnOwner: environment.app.asnowner,
      eventTimestamp: new Date().valueOf(),
    };
  }

  public getUserInfo(): UserInfo {
    const userID = this.localStorage.getItem(LocalStorageKeys.ACCOUNT_ID) ?? '';
    const profileID = this.localStorage.getItem(LocalStorageKeys.SELECTED_PROFILE_ID) ?? '';
    const userTypeValue = isLoggedIn(this.localStorage) ? UserType.REGISTERED : UserType.ANONYMOUS;
    return {
      userID,
      profileID,
      userType: userTypeValue,
      profileType: getProfileType(this.localStorage),
    }
  }

  public getLanguageInfo(): LanguageInfo {
    return {
      contentLanguage: getSelectedWatchLangName(this.localStorage),
      displayLanguage: getSelectedDisplayLangName(this.localStorage),
    };
  }

  public getLoginInfo(): { silentLogin: boolean } {
    return {
      silentLogin: this.localStorage.getItem(LocalStorageKeys.ACCESS_TOKEN) !== null,
    }
  }

  public getSubscriptionInfo(): { baseSubscription: string } {
    return {
       baseSubscription: 'free',
    }
  }

  public getErrorInfo(error: any, errorType: ErrorEvents): ErrorInfo {
    let errorAttributes: ErrorInfo = {
      errorCode: 'NA',
      errorDescription: 'NA',
      errorMessage: 'NA',
      internalError: 'NA',
      errorType,
    };
    try {
      if (error && error.doesBelongToCategory) {
        // lib errors
        errorAttributes.errorCode = error.errorCode?.toString();
        errorAttributes.errorDescription = error.errorMessage;
        errorAttributes.errorMessage = error.errorMessage;
        errorAttributes.internalError = error.internalError;
      } else if (error instanceof QPError) {
        errorAttributes.errorCode = error.error.errorCode?.toString();
        errorAttributes.errorDescription = error.error?.errorMessage;
        errorAttributes.errorMessage = error.message;
        errorAttributes.internalError = error?.error;
      } else if (error instanceof ErrorBase) {
        errorAttributes.errorCode = '46200001';
        errorAttributes.errorDescription = error.message?.message;
        errorAttributes.errorMessage = error?.message?.message;
        errorAttributes.internalError = error?.message?.stack;
      } else if (error instanceof Error) {
        errorAttributes.errorCode = '46200002';
        errorAttributes.errorDescription = error.message;
        errorAttributes.errorMessage = error.message;
        errorAttributes.internalError = error;
      } else {
        errorAttributes.errorCode = error?.errorCode?.toString();
        errorAttributes.errorDescription = error.errorMessage;
        errorAttributes.errorMessage = error.errorMessage;
        errorAttributes.internalError = error;
      }
    } catch {}
    return errorAttributes;
  }
}
